/**
 * The selection banner's Clear and Cut, shared so a page's hotkeys do exactly what the buttons do
 * (including the `selection-banner-cleared` / `selection-banner-cut` events pages listen for).
 */
import {
  clearSelection,
  cutSelection,
  hasSelection,
  selectedAggregateEvents,
  selectedEvents,
  selectedRange,
  type SelectionDomain,
} from "../store/selectionStore";
import { runWithScatterSelectionBusyCursor } from "./scatterSelectionBusyCursor";

/**
 * Which selection domain the actions act on. The banner is one app-wide singleton with no page context,
 * so it reads the domain off the store: performance pages keep their selection in `selectedAggregateEvents`,
 * maneuver pages in `selectedEvents`. Maneuver wins when both hold a selection (e.g. split view).
 */
export function selectionBannerDomain(): SelectionDomain {
  return selectedEvents().length === 0 && selectedAggregateEvents().length > 0 ? "aggregate" : "maneuver";
}

/** Events exist to hide/cut in either domain. */
export function hasSelectedEventsToAct(): boolean {
  return selectedEvents().length > 0 || selectedAggregateEvents().length > 0;
}

/** When the banner shows its Cut button. */
export function canCutSelection(): boolean {
  return hasSelection() && (hasSelectedEventsToAct() || (selectedRange()?.length ?? 0) > 0);
}

export function runSelectionBannerClear(): void {
  runWithScatterSelectionBusyCursor(() => {
    clearSelection();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("selection-banner-cleared"));
    }
  });
}

/** A page that owns the cut cancels `selection-banner-cut`; otherwise the store cuts. */
export function runSelectionBannerCut(): void {
  runWithScatterSelectionBusyCursor(() => {
    if (typeof window !== "undefined") {
      const cutEvent = new CustomEvent("selection-banner-cut", { cancelable: true });
      window.dispatchEvent(cutEvent);
      if (!cutEvent.defaultPrevented) {
        cutSelection(selectionBannerDomain());
      }
    } else {
      cutSelection(selectionBannerDomain());
    }
  });
}
